import React, { useRef } from "react"
import PropTypes from "prop-types"
import cn from "classnames"

import { Button } from "@asphalt-react/button"
import { Menu, Cross } from "@asphalt-react/iconpack"
import { KEYCODES, focusUtil } from "@asphalt-react/helper"

import styles from "./appbar.module.css"
import { useAppbar } from "./useAppbar.js"
import { BaseAppbar } from "./base-appbar.js"
import { AppbarHead } from "./appbar-head.js"
import { AppbarBody } from "./appbar-body.js"
import { AppbarTail } from "./appbar-tail.js"

const { focusTrap } = focusUtil

export const Appbar = ({
  children,
  head = null,
  tail = null,
  dismissLabel = "close appbar navigation",
  menuLabel = "open appbar navigation",
  defaultVisible = false,
  ...props
}) => {
  // filtering out style and className
  /* eslint-disable-next-line react/prop-types */
  const { style, className, ...rest } = props

  const hasChildren = Boolean(children)
  const { visible, open, close } = useAppbar({ defaultVisible })
  const appbarRef = useRef(null)

  Appbar.close = close

  /**
   * handle keyDown events
   *
   * @param {Object} event
   */
  const onKeyDown = (event) => {
    if (visible) {
      const { ESC, TAB } = KEYCODES
      // do nothing if neither `ESC` or `TAB`keys are pressed
      if (!(event.keyCode === ESC || event.keyCode === TAB)) return

      // Handle Esc keydown event
      if (event.keyCode === ESC) {
        close()
      }

      // Handle Tab keydown event
      if (event.keyCode === TAB) {
        focusTrap(appbarRef.current, event)
      }
    }
  }

  const wrapperClass = cn(styles.Appbar, {
    [styles.AppbarExpanded]: visible,
  })
  const contentClass = cn(styles.content, { [styles.contentExpanded]: visible })
  const headClass = cn(
    styles.head,
    { [styles.headExpanded]: visible },
    { [styles.noContent]: !hasChildren }
  )
  const tailClass = cn(styles.tail, { [styles.tailExpanded]: visible })

  return (
    <div className={wrapperClass}>
      <BaseAppbar {...rest} onKeyDown={onKeyDown} ref={appbarRef}>
        <div className={headClass}>
          <AppbarHead>
            {hasChildren &&
              (visible ? (
                <span className={styles.toggleBtn}>
                  <Button
                    size="l"
                    icon
                    system
                    nude
                    compact
                    onClick={close}
                    aria-haspopup="menu"
                    aria-expanded
                    aria-label={dismissLabel}
                    id="appbar-cross"
                  >
                    <Cross />
                  </Button>
                </span>
              ) : (
                <span className={styles.toggleBtn}>
                  <Button
                    size="l"
                    icon
                    system
                    nude
                    compact
                    onClick={open}
                    aria-haspopup="menu"
                    aria-expanded={false}
                    aria-label={menuLabel}
                    id="appbar-hamburger"
                  >
                    <Menu />
                  </Button>
                </span>
              ))}
            {head}
          </AppbarHead>
        </div>
        {hasChildren && (
          <div className={contentClass}>
            <AppbarBody>{children}</AppbarBody>
          </div>
        )}
        {tail && (
          <div className={tailClass}>
            <AppbarTail>{tail}</AppbarTail>
          </div>
        )}
      </BaseAppbar>
    </div>
  )
}

Appbar.displayName = "Appbar"

Appbar.propTypes = {
  /**
   * React node to render in the Appbar content.
   */
  children: PropTypes.node,
  /**
   * Content to display in the Appbar head.
   */
  head: PropTypes.node,
  /**
   * Content to display in the Appbar tail.
   */
  tail: PropTypes.node,
  /**
   * "aria-label" for the button to close Appbar in smaller screens.
   */
  dismissLabel: PropTypes.string,
  /**
   * "aria-label" for the button to open Appbar in smaller screens.
   */
  menuLabel: PropTypes.string,
  /**
   * Appbar shows the body section in the initial state.
   */
  defaultVisible: PropTypes.bool,
}

Appbar.defaultProps = {
  head: null,
  tail: null,
  dismissLabel: "close appbar navigation",
  menuLabel: "open appbar navigation",
  defaultVisible: false,
}
